import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Wrench,
  Disc,
  Cog,
  Zap,
  Cable,
  ScanLine,
  Phone,
  MapPin,
  ArrowRight,
  Star,
  Check,
} from "lucide-react";
import { Toaster } from "sonner";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { Navbar, Footer } from "./App";
import { AnimationEmbed } from "./components/AnimationEmbed";


const API = process.env.REACT_APP_BACKEND_URL;

const RepEntretienPage = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [homeContent, setHomeContent] = useState({});
  const [reviews, setReviews] = useState({ rating: 4.8, user_ratings_total: 107, url: "" });
  const [cms, setCms] = useState({});
  const contact = useContactInfo();

  const cmsSeo = cms?.seo || {};
  useSiteMeta({
    title: cmsSeo.title || "Réparation & Entretien Auto Clermont-Ferrand | ATR",
    description:
      cmsSeo.description ||
      "Garage réparation et entretien toutes marques à Clermont-Ferrand. Spécialistes véhicules allemands : Audi, BMW, Mercedes, VW, Porsche. Devis gratuit au 06 49 07 08 20.",
    canonical_path: "/reparation-entretien-clermont-ferrand",
    og_image: `${window.location.origin}/og/entretien.jpg`,
    og_image_alt: "Réparation & entretien auto — Auvergne Tech Racing, Clermont-Ferrand",
  });

  useEffect(() => {
    axios.get(`${API}/api/business-hours`).then((r) => setBusinessHours(r.data)).catch(() => {});
    axios.get(`${API}/api/content/home_landing`).then((r) => setHomeContent(r.data)).catch(() => {});
    axios.get(`${API}/api/content/rep_entretien_page`).then((r) => setCms(r.data || {})).catch(() => {});
    axios
      .get(`${API}/api/google-reviews`)
      .then((r) =>
        setReviews({
          rating: r.data.rating ?? 4.8,
          user_ratings_total: r.data.user_ratings_total ?? 107,
          url: r.data.url || "",
        })
      )
      .catch(() => {});
  }, []);

  const defaultServiceIcons = [Wrench, Disc, Cable, Cog, ScanLine];
  const hero = cms.hero || {};
  const brandsSection = cms.brands_section || {};
  const finalCta = cms.final_cta || {};

  const cmsServices = Array.isArray(cms.services) && cms.services.length ? cms.services : null;
  const services = cmsServices
    ? cmsServices.map((s, i) => ({ icon: defaultServiceIcons[i] || Wrench, title: s.title, description: s.description }))
    : [
        { icon: Wrench, title: "Entretien courant — toutes marques", description: "Vidange, filtres, bougies, courroie d'accessoires, liquides de frein et de refroidissement. Nous assurons l'entretien complet de votre véhicule dans les délais constructeur pour conserver votre garantie constructeur." },
        { icon: Disc, title: "Freinage", description: "Remplacement des plaquettes, disques, tambours et étriers. Purge et remplacement du liquide de frein. Contrôle complet du système de freinage." },
        { icon: Cable, title: "Distribution — chaîne & courroie", description: "Remplacement de la chaîne ou courroie de distribution, kit complet (tendeurs, galets, pompe à eau). Intervention préventive recommandée selon le kilométrage constructeur. Devis gratuit." },
        { icon: Cog, title: "Embrayage & boîte de vitesses", description: "Remplacement d'embrayage, kit complet volant moteur bimasse. Diagnostic boîte de vitesses manuelle et automatique." },
        { icon: ScanLine, title: "Diagnostic électronique", description: "Lecture et effacement des codes défauts sur tous systèmes (moteur, boîte, ABS, airbags). Diagnostic approfondi avec outils professionnels multimarques." },
      ];

  const brands = Array.isArray(brandsSection.brands) && brandsSection.brands.length
    ? brandsSection.brands
    : ["Audi", "BMW", "Mercedes-Benz", "Volkswagen", "Porsche"];

  const formatAdvantage = (s) =>
    (s || "").replace(/\{rating\}/g, String(reviews.rating).replace(".", ",")).replace(/\{reviews_count\}/g, String(reviews.user_ratings_total));

  const advantages = (Array.isArray(cms.advantages) && cms.advantages.length ? cms.advantages : [
    "Garage indépendant : tarifs justes, sans les marges du réseau officiel",
    "Techniciens passionnés avec une vraie culture automobile",
    "{rating}/5 sur Google — plus de {reviews_count} avis clients",
    "Devis gratuit avant toute intervention",
    "Délais respectés, pièces de qualité d'origine ou équivalentes",
  ]).map(formatAdvantage);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    name: `${contact.business_name} — Garage Réparation & Entretien`,
    description:
      "Garage indépendant à Clermont-Ferrand — réparation et entretien toutes marques, spécialiste véhicules allemands (Audi, BMW, Mercedes, Volkswagen, Porsche).",
    telephone: contact.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: contact.street,
      addressLocality: contact.city,
      postalCode: contact.postal_code,
      addressCountry: contact.country,
    },
    areaServed: { "@type": "City", name: contact.city },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviews.rating || 4.8,
      reviewCount: reviews.user_ratings_total || 107,
      bestRating: 5,
      worstRating: 1,
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Prestations mécanique",
      itemListElement: services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title },
      })),
    },
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="rep-entretien-page">
      <script
        type="application/ld+json"
        data-testid="rep-entretien-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Toaster position="top-right" richColors />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        {cms.hero_bg_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${cms.hero_bg_image_url}')` }}
            data-testid="rep-entretien-hero-bg"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3B30]/[0.08] via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-6">
            <Wrench size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">
              {hero.badge || "Toutes marques · Spécialiste allemand"}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-6 leading-tight"
            data-testid="rep-entretien-h1"
          >
            {cmsSeo.h1_prefix || hero.h1_top || "Réparation & Entretien Auto"}
            <br />
            <span className="text-[#FF3B30]">{cmsSeo.h1_highlight || hero.h1_highlight || "à Clermont-Ferrand"}</span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-3xl">
            {hero.intro || "Auvergne Tech Racing est votre garage de proximité à Clermont-Ferrand pour l'entretien courant et la réparation de votre véhicule. Toutes marques acceptées, avec une expertise particulière sur les véhicules allemands. Diagnostic précis, pièces de qualité, tarifs transparents."}
          </p>

          <div className="flex flex-wrap gap-3 items-center mb-8">
            <a
              href={`tel:${contact.phone}`}
              data-testid="rep-entretien-cta-call"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              <Phone size={16} />
              {hero.cta_phone_label || "06 49 07 08 20"}
            </a>
            <a
              href="/#booking"
              data-testid="rep-entretien-cta-book"
              className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-white font-semibold border border-white/20 px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              {hero.cta_book_label || "Devis gratuit"}
              <ArrowRight size={16} />
            </a>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-sm" data-testid="rep-entretien-reviews-badge">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className="fill-yellow-400" />
              ))}
            </div>
            <span>
              {String(reviews.rating).replace(".", ",")}/5 — {reviews.user_ratings_total} avis Google
            </span>
          </div>
        </div>
      </section>

      {/* Animation Entretien */}
      <section className="py-12 md:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="entretien-page-animation-section">
        <div className="max-w-5xl mx-auto px-6">
          <AnimationEmbed src="/entretien.html" title="Entretien & Réparation Auto" testId="entretien-animation" />
        </div>
      </section>

      {/* Services / prestations */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight text-center">
            {cms.services_title_before || "Nos prestations"} <span className="text-[#FF3B30]">{cms.services_title_highlight || "mécanique"}</span>
          </h2>
          <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">
            {cms.services_subtitle || "De l'entretien courant à la réparation complexe — tout est fait en atelier avec du matériel professionnel."}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`rep-entretien-service-${i}`}
                  className="bg-black/40 border border-white/[0.06] rounded-xl p-6 hover:border-[#FF3B30]/30 transition-colors flex flex-col"
                >
                  <Icon className="text-[#FF3B30] mb-4" size={32} />
                  <h3 className="text-white font-bold text-xl mb-3">{s.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed flex-1">{s.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spécialité véhicules allemands */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-4">
            <Zap size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">{brandsSection.badge || "Notre spécialité"}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            {brandsSection.title_top || "Spécialité :"} <span className="text-[#FF3B30]">{brandsSection.title_highlight || "véhicules allemands"}</span>
          </h2>
          <p className="text-white/70 leading-relaxed max-w-3xl mb-8">
            {brandsSection.description || "Nous sommes particulièrement équipés pour les marques allemandes, reconnues pour leur sophistication mécanique et électronique. Notre matériel de diagnostic couvre l'ensemble des systèmes propriétaires de ces constructeurs."}
          </p>

          <p className="text-white/50 text-sm uppercase tracking-wider mb-4 font-bold">{brandsSection.brands_subtitle || "Marques maîtrisées"}</p>
          <div className="flex flex-wrap gap-3 mb-10">
            {brands.map((b, i) => (
              <span
                key={b}
                data-testid={`rep-entretien-brand-${i}`}
                className="inline-flex items-center gap-2 bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-lg px-4 py-2.5 text-white font-semibold text-sm transition-colors"
              >
                <span className="w-2 h-2 bg-[#FF3B30] rounded-full" />
                {b}
              </span>
            ))}
          </div>

          <p className="text-white/70 leading-relaxed max-w-3xl">
            {brandsSection.outro || "Que ce soit pour un entretien courant, une réparation complexe ou une mise à niveau, vous bénéficiez d'une intervention de niveau spécialiste sans passer par un concessionnaire."}
          </p>
        </div>
      </section>

      {/* Avantages ATR */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 tracking-tight text-center">
            {cms.advantages_title_before || "Pourquoi choisir"} <span className="text-[#FF3B30]">{cms.advantages_title_highlight || "ATR"}</span>{cms.advantages_title_after || " pour votre mécanique ?"}
          </h2>

          <ul className="space-y-4">
            {advantages.map((a, i) => (
              <li
                key={i}
                data-testid={`rep-entretien-advantage-${i}`}
                className="flex items-start gap-3 bg-black/40 border border-white/[0.06] rounded-lg p-4"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-[#FF3B30]" />
                </div>
                <span className="text-white/80 leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Prendre rendez-vous */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">{finalCta.title || "Prendre rendez-vous"}</h2>
          <p className="text-white/70 mb-10 max-w-2xl mx-auto">{finalCta.subtitle || "Contactez-nous pour un devis ou pour réserver un créneau."}</p>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
            <a
              href={`tel:${contact.phone}`}
              data-testid="rep-entretien-contact-phone"
              className="bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-xl p-6 transition-colors flex items-center gap-4 text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
                <Phone size={20} className="text-[#FF3B30]" />
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Téléphone</div>
                <div className="text-white font-bold text-lg">06 49 07 08 20</div>
              </div>
            </a>

            <a
              href={reviews.url || "https://maps.google.com/?cid=1681708394347082412"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rep-entretien-contact-address"
              className="bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-xl p-6 transition-colors flex items-center gap-4 text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
                <MapPin size={20} className="text-[#FF3B30]" />
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Adresse</div>
                <div className="text-white font-bold">{contact.street}</div>
                <div className="text-white/60 text-sm">{contact.postal_code} {contact.city}</div>
              </div>
            </a>
          </div>

          <a
            href="/#booking"
            data-testid="rep-entretien-cta-book-bottom"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-8 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
          >
            {finalCta.button_label || "Réserver un créneau en ligne"}
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer businessHours={businessHours} content={homeContent} />
    </div>
  );
};

export default RepEntretienPage;
